import React, { useMemo } from "react";

import { faCheckCircle, faClock } from "@fortawesome/pro-solid-svg-icons";
import { faDownload } from "@fortawesome/pro-solid-svg-icons/faDownload";
import { faInfoCircle } from "@fortawesome/pro-solid-svg-icons/faInfoCircle";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ColumnType } from "antd/es/table";
import Tag from "antd/es/tag";
import Tooltip from "antd/es/tooltip";
import { FormattedMessage, IntlShape } from "react-intl";

import FieldFeature from "@mapmycustomers/shared/enum/fieldModel/FieldFeature";
import IntegrationSyncDirection from "@mapmycustomers/shared/enum/integrations/IntegrationSyncDirection";
import IntegrationSyncStatus from "@mapmycustomers/shared/enum/integrations/IntegrationSyncStatus";
import SortOrder from "@mapmycustomers/shared/enum/SortOrder";
import SyncType from "@mapmycustomers/shared/enum/SyncType";
import ColumnModel from "@mapmycustomers/shared/types/viewModel/internalModel/ColumnModel";
import { Button } from "@mapmycustomers/ui";

import TextWithInfo from "@app/component/typography/TextWithInfo";
import { downloadFile as downloadFileAction } from "@app/store/files/actions";
import IntegrationSync, { IntegrationSyncMetadata } from "@app/types/integrations/IntegrationSync";
import integrationFieldModel from "@app/util/fieldModel/IntegrationFieldModel";
import { formatRawDate } from "@app/util/formatters";
import getIntegrationSyncFileName from "@app/util/integrations/getIntegrationSyncFileName";

import { infoMessages } from "../infoMessages";

import styles from "./useIntegrationSyncsTableColumnConfig.module.scss";

const useIntegrationSyncsTableColumnConfig = (
  intl: IntlShape,
  downloadFile: typeof downloadFileAction,
  columns: ColumnModel,
  syncDirection: string | undefined,
  sort?: string,
  sortOrder?: SortOrder
): ColumnType<IntegrationSync>[] =>
  useMemo(() => {
    const visibleFieldsNames = new Set(
      columns.filter(({ visible }) => visible).map(({ field }) => field.name)
    );

    const completedWithErrorsCheck = (record: IntegrationSync) => {
      return [
        ...Object.values((record.metadata.incoming ?? {}) as IntegrationSyncMetadata),
        ...Object.values((record.metadata.outgoing ?? {}) as IntegrationSyncMetadata),
      ].some((row) => row["errored"] > 0);
    };

    return [
      {
        dataIndex: "createdAt",
        fixed: "left",
        key: "createdAt",
        render: (createdAt: string, { syncType }: IntegrationSync) => {
          return (
            <div className={styles.createdAt}>
              <div>{formatRawDate(createdAt, "PPPp", "-")}</div>
              {[SyncType.POST_SYNC, SyncType.PRE_SYNC].includes(syncType) && (
                <Tooltip
                  title={
                    syncType === SyncType.POST_SYNC
                      ? intl.formatMessage(
                          {
                            id: "integration.field.createdAt.syncType.postSync.tooltip",
                            defaultMessage:
                              "This sync updates all data based on your recent change to users and/or fields. Please note that <b>any scheduled syncs will be skipped</b> during the re-sync process.",
                            description: "Post sync type tooltip",
                          },
                          { b: (text: string) => <b>{text}</b> }
                        )
                      : intl.formatMessage(
                          {
                            id: "integration.field.createdAt.syncType.preSync.tooltip",
                            defaultMessage:
                              "This sync prepares your data for changes to your user and fields mapping. It ensures that any pending edits to records are updated before your desired changes are made. Please note that  <b>any scheduled syncs will be skipped</b> during the re-sync process.",
                            description: "Pre sync type tooltip",
                          },
                          { b: (text: string) => <b>{text}</b> }
                        )
                  }
                >
                  <Tag className={styles.syncTypeTag}>
                    <FontAwesomeIcon className={styles.icon} icon={faInfoCircle} size="sm" />
                    {syncType === SyncType.POST_SYNC
                      ? intl.formatMessage({
                          id: "integration.field.createdAt.syncType.postSync.text",
                          defaultMessage: "Update Sync",
                          description: "Post sync type text",
                        })
                      : intl.formatMessage({
                          id: "integration.field.createdAt.syncType.preSync.text",
                          defaultMessage: "Preparation Sync",
                          description: "Pre sync type text",
                        })}
                  </Tag>
                </Tooltip>
              )}
            </div>
          );
        },
        showSorterTooltip: false,
        sorter: true,
        sortOrder:
          sort === "createdAt" ? (sortOrder === SortOrder.ASC ? "ascend" : "descend") : undefined,
        title: intl.formatMessage({
          id: "integration.field.createdAt",
          defaultMessage: "Date and Frequency",
          description: "date field on Integration",
        }),
        width: "300px",
      },
      {
        dataIndex: "status",
        fixed: "left",
        key: "status",
        render: (status: IntegrationSyncStatus, record: IntegrationSync) => {
          const hasErrors = completedWithErrorsCheck(record);
          return (
            <Tag
              color={
                status === IntegrationSyncStatus.COMPLETED
                  ? "success"
                  : status === IntegrationSyncStatus.ERROR || hasErrors
                  ? "error"
                  : "warning"
              }
            >
              <FontAwesomeIcon
                className={styles.tag}
                icon={
                  status === IntegrationSyncStatus.COMPLETED
                    ? faCheckCircle
                    : status === IntegrationSyncStatus.ERROR || hasErrors
                    ? faInfoCircle
                    : faClock
                }
              />
              <FormattedMessage
                id="integrations.syncArchive.column.status"
                defaultMessage="{status, select, completed {Success} errored {Error} other {Syncing}}"
                description="Text of status message"
                values={{
                  status:
                    status === IntegrationSyncStatus.COMPLETED
                      ? "completed"
                      : status === IntegrationSyncStatus.ERROR
                      ? "errored"
                      : "other",
                }}
              />
            </Tag>
          );
        },
        showSorterTooltip: false,
        sorter: true,
        sortOrder:
          sort === "status" ? (sortOrder === SortOrder.ASC ? "ascend" : "descend") : undefined,
        title: intl.formatMessage({
          id: "integration.field.status",
          defaultMessage: "Status",
          description: "Status field on Integration",
        }),
        width: "200px",
      },
      {
        dataIndex: "syncStatsFileId",
        fixed: "left",
        key: "id",
        render: (syncStatsFileId: number | undefined, integrationSync: IntegrationSync) =>
          syncStatsFileId ? (
            <Button
              icon={<FontAwesomeIcon className={styles.downloadIcon} icon={faDownload} />}
              iconOnly
              onClick={() =>
                downloadFile({
                  fileId: syncStatsFileId,
                  fileName: getIntegrationSyncFileName(intl, integrationSync),
                })
              }
              type="link"
            />
          ) : null,
        title: intl.formatMessage({
          id: "integration.field.export",
          defaultMessage: "Export",
          description: "Export field on Integration",
        }),
        width: "64px",
      },
      ...integrationFieldModel.fields
        .filter(
          (field) =>
            field.hasFeature(FieldFeature.METRIC) &&
            visibleFieldsNames.has(field.name) &&
            (syncDirection === IntegrationSyncDirection.SERVICE_TO_MMC
              ? field.hasFeature(FieldFeature.INCOMING)
              : syncDirection === IntegrationSyncDirection.MMC_TO_SERVICE
              ? field.hasFeature(FieldFeature.OUTGOING)
              : true)
        )
        .map<ColumnType<IntegrationSync>>((field) => ({
          dataIndex: ["metaData", field.name],
          key: field.name,
          render: (_, record) => {
            return field.getFormattedValueFor(record);
          },
          showSorterTooltip: false,
          sorter: true,
          sortOrder:
            sort === field.name ? (sortOrder === SortOrder.ASC ? "ascend" : "descend") : undefined,
          title: (
            <TextWithInfo info={intl.formatMessage(infoMessages[field.name])}>
              {field.displayName}
            </TextWithInfo>
          ),
          width: "300px",
        })),
    ];
  }, [downloadFile, intl, columns, syncDirection, sort, sortOrder]);

export default useIntegrationSyncsTableColumnConfig;
